import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  GitCompare, Plus, Trash2, Edit2, Copy, ExternalLink,
  Loader2, Image, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComparisonsPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', slug: '', description: '', public: true, pairs: [] });
  const [newPair, setNewPair] = useState({ before_photo_key: '', after_photo_key: '', caption: '' });

  useEffect(() => { fetchPages(); }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/comparisons`);
      setPages(res.data.comparisons || []);
    } catch { toast.error('Blad ladowania'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/comparisons/${editing}`, form);
        toast.success('Zaktualizowano');
      } else {
        await axios.post(`${API}/comparisons`, form);
        toast.success('Utworzono strone porownania');
      }
      setShowCreate(false);
      setEditing(null);
      setForm({ title: '', slug: '', description: '', public: true, pairs: [] });
      fetchPages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blad zapisu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunac strone porownania?')) return;
    try {
      await axios.delete(`${API}/comparisons/${id}`);
      toast.success('Usunieto');
      fetchPages();
    } catch { toast.error('Blad usuwania'); }
  };

  const handleEdit = (page) => {
    setForm({
      title: page.title,
      slug: page.slug,
      description: page.description || '',
      public: page.public,
      pairs: page.pairs || [],
    });
    setEditing(page.id);
    setShowCreate(true);
  };

  const addPair = () => {
    if (!newPair.before_photo_key || !newPair.after_photo_key) return;
    setForm({ ...form, pairs: [...form.pairs, { ...newPair }] });
    setNewPair({ before_photo_key: '', after_photo_key: '', caption: '' });
  };

  const removePair = (idx) => {
    setForm({ ...form, pairs: form.pairs.filter((_, i) => i !== idx) });
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/compare/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link skopiowany');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar activePath="/comparisons" />
        <main className="lg:ml-64 p-6 lg:p-10 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/comparisons" />
      <main className="lg:ml-64 p-6 lg:p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <GitCompare className="w-8 h-8" />
              Before & After
            </h1>
            <p className="text-muted-foreground mt-1">Strony porownawcze zdjec</p>
          </div>
          <Button onClick={() => { setEditing(null); setForm({ title: '', slug: '', description: '', public: true, pairs: [] }); setShowCreate(true); }}>
            <Plus className="w-4 h-4 mr-2" />Nowa strona
          </Button>
        </div>

        {pages.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <GitCompare className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Brak stron porownawczych</p>
            <p className="text-sm mt-1">Stworz strone before/after zeby pokazac swoje umiejetnosci retuszu</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pages.map((page, i) => (
              <motion.div key={page.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{page.title}</CardTitle>
                      <Badge variant={page.public ? 'default' : 'secondary'}>
                        {page.public ? <><Eye className="w-3 h-3 mr-1" />Publiczna</> : <><EyeOff className="w-3 h-3 mr-1" />Prywatna</>}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-3">{page.description}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                      <Image className="w-4 h-4" />
                      <span>{page.pairs?.length || 0} par zdjec</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4">/{page.slug}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(page)}><Edit2 className="w-3 h-3 mr-1" />Edytuj</Button>
                      <Button size="sm" variant="outline" onClick={() => copyLink(page.slug)}><Copy className="w-3 h-3 mr-1" />Link</Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(page.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edytuj strone' : 'Nowa strona porownawcza'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Tytul</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Retusz slubny" /></div>
                <div><Label>Slug (URL)</Label><Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="retusz-slubny" /></div>
              </div>
              <div><Label>Opis</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Opis strony..." rows={2} /></div>
              <div className="flex items-center gap-2">
                <Switch checked={form.public} onCheckedChange={v => setForm({ ...form, public: v })} />
                <Label>Publiczna</Label>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Pary zdjec ({form.pairs.length})</h3>
                {form.pairs.map((pair, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2 p-2 bg-secondary/50 rounded">
                    <span className="text-sm flex-1">{pair.caption || `Para ${idx + 1}`}</span>
                    <span className="text-xs text-muted-foreground">{pair.before_photo_key} → {pair.after_photo_key}</span>
                    <Button size="sm" variant="ghost" onClick={() => removePair(idx)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <Input value={newPair.before_photo_key} onChange={e => setNewPair({ ...newPair, before_photo_key: e.target.value })} placeholder="Before (klucz)" />
                  <Input value={newPair.after_photo_key} onChange={e => setNewPair({ ...newPair, after_photo_key: e.target.value })} placeholder="After (klucz)" />
                  <div className="flex gap-1">
                    <Input value={newPair.caption} onChange={e => setNewPair({ ...newPair, caption: e.target.value })} placeholder="Podpis" />
                    <Button size="sm" onClick={addPair}><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreate(false)}>Anuluj</Button>
                <Button onClick={handleSave} disabled={!form.title || !form.slug}>{editing ? 'Zapisz' : 'Utworz'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
